import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'react-use';

import useTracking from '@zen/utils/hooks/useTracking';

import { TableConfigurationTrackingAction, TableConfigurationTrackingCategory } from '../types';
import type { StateUpdater, VisibilityState } from '../utils/columnStateConverters';
import { hiddenColumnsToVisibilityState, resolveUpdater, visibilityStateToHiddenColumns } from '../utils/columnStateConverters';

interface ColumnConfiguration {
  // TanStack-compatible API
  columnVisibility: VisibilityState;
  // Legacy API (kept for backward compatibility)
  hiddenColumns: string[];
  onColumnVisibilityChange: (updater: StateUpdater<VisibilityState>) => void;
  setHiddenColumns: (hiddenColumns: string[]) => void;
}

const STORAGE_KEY = 'table-hidden-columns';

export const useColumnConfiguration = (tableId: string, initialHiddenColumns: string[] = []): ColumnConfiguration => {
  const [storedHiddenColumns, setStoredHiddenColumns] = useLocalStorage<Record<string, string[]>>(STORAGE_KEY, {});

  const { trackEvent } = useTracking();

  const setHiddenColumns = useCallback(
    (hiddenColumns: string[]) => {
      trackEvent({
        category: TableConfigurationTrackingCategory,
        action: TableConfigurationTrackingAction.CUSTOMISE,
        label: tableId,
        properties: {
          tableId,
          hiddenColumns
        }
      });

      setStoredHiddenColumns((prev) => ({ ...prev, [tableId]: hiddenColumns }));
    },
    [tableId, trackEvent, setStoredHiddenColumns]
  );

  const hiddenColumns: string[] = storedHiddenColumns?.[tableId] || initialHiddenColumns;

  // TanStack-compatible visibility state
  const columnVisibility = useMemo(() => hiddenColumnsToVisibilityState(hiddenColumns), [hiddenColumns]);

  // TanStack-compatible onChange handler
  const onColumnVisibilityChange = useCallback(
    (updater: StateUpdater<VisibilityState>) => {
      const newVisibility = resolveUpdater(updater, columnVisibility);
      const newHiddenColumns = visibilityStateToHiddenColumns(newVisibility);

      setHiddenColumns(newHiddenColumns);
    },
    [columnVisibility, setHiddenColumns]
  );

  return {
    hiddenColumns,
    setHiddenColumns,
    columnVisibility,
    onColumnVisibilityChange
  };
};
